import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Search, ArrowRight, Truck, MapPin, Shield, Clock, ChevronRight, Star } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import SendtooLogo from "@/components/shared/SendtooLogo";
import { API } from "@/utils/api";
import axios from "axios";

const stats = [
  { value: "50K+", label: "Shipments Delivered" },
  { value: "120+", label: "Cities Covered" },
  { value: "98%", label: "On-Time Delivery" },
  { value: "5K+", label: "Happy Customers" },
];

const services = [
  { icon: Truck, title: "Express Delivery", desc: "Same-day and next-day delivery options for urgent shipments across India." },
  { icon: Package, title: "Parcel Booking", desc: "Easy online booking with real-time price calculation based on weight and destination." },
  { icon: MapPin, title: "Live Tracking", desc: "Track your shipment at every step with our real-time tracking system." },
  { icon: Shield, title: "Secure Handling", desc: "Every package is insured and handled with utmost care and precision." },
  { icon: Clock, title: "24/7 Support", desc: "Round-the-clock customer support to assist you with any queries." },
  { icon: Star, title: "Trusted Network", desc: "A network of verified agents ensuring reliable delivery across all zones." },
];

const steps = [
  { step: "01", title: "Book Online", desc: "Fill in the sender & receiver details, package info, and get instant pricing." },
  { step: "02", title: "Agent Picks Up", desc: "A verified Sendtoo agent picks up your package from your doorstep." },
  { step: "03", title: "Track in Real-Time", desc: "Monitor every movement of your shipment with our live tracking system." },
  { step: "04", title: "Delivered!", desc: "Your package is delivered safely and on time to the destination." },
];

export default function Home() {
  const [trackingId, setTrackingId] = useState("");
  const [tracking, setTracking] = useState(null);
  const [trackError, setTrackError] = useState("");
  const [trackLoading, setTrackLoading] = useState(false);
  const navigate = useNavigate();

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    setTrackLoading(true);
    setTrackError("");
    setTracking(null);
    try {
      const res = await axios.get(`${API}/track/${trackingId.trim().toUpperCase()}`);
      setTracking(res.data);
    } catch (err) {
      setTrackError(err.response?.data?.detail || "Shipment not found. Please check the tracking ID.");
    } finally {
      setTrackLoading(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A183D] via-[#1B3FA0] to-[#0EA5E9] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1717700299591-470e043edc9c?w=1400&q=80')", backgroundSize: "cover", backgroundPosition: "center" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <SendtooLogo size="2xl" className="mb-8" />
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white uppercase tracking-tight leading-none mb-4">
              Deliver<br />Without<br />Limits
            </h1>
            <p className="text-blue-100 text-lg mb-10 leading-relaxed">
              India's most reliable logistics platform. Fast pickups, real-time tracking, and transparent pricing — all in one place.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#F97316] text-white font-semibold uppercase tracking-wide rounded-sm hover:bg-orange-600 transition-all active:scale-95"
              >
                Book Shipment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/auth/register"
                data-testid="hero-signup-btn"
                className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 border border-white/30 text-white font-semibold uppercase tracking-wide rounded-sm hover:bg-white/20 transition-all"
              >
                Create Account
              </Link>
            </div>
          </motion.div>

          {/* Tracking Widget */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 max-w-xl"
          >
            <h3 className="text-white font-heading font-semibold text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
              <Search className="w-5 h-5" /> Track Your Shipment
            </h3>
            <form onSubmit={handleTrack} className="flex gap-3">
              <input
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder="Enter tracking ID (e.g. STXABC12345)"
                data-testid="hero-tracking-input"
                className="flex-1 px-4 py-3 rounded-sm bg-white text-slate-800 placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <button
                type="submit"
                data-testid="hero-track-btn"
                disabled={trackLoading}
                className="px-6 py-3 bg-[#F97316] text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-70 uppercase text-sm"
              >
                {trackLoading ? "..." : "Track"}
              </button>
            </form>
            {trackError && <p className="mt-2 text-red-300 text-sm">{trackError}</p>}
            {tracking && (
              <div className="mt-4 bg-white rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-500 uppercase">Tracking ID</span>
                  <span className="font-heading font-bold text-brand-500">{tracking.tracking_id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500 uppercase">Status</span>
                  <span className="px-2 py-0.5 bg-brand-50 text-brand-500 rounded-full text-xs font-bold uppercase">{tracking.status?.replace(/_/g, " ")}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-500">{tracking.origin?.city} → {tracking.destination?.city}</span>
                  <Link to="/track" className="text-xs text-brand-500 hover:underline font-medium">Full Details →</Link>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="font-heading text-4xl font-bold text-white">{s.value}</div>
                <div className="text-blue-100 text-sm mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-2">What We Offer</p>
            <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase">Our Services</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-l-4 border-l-brand-500"
              >
                <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-brand-500" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-2">Simple Process</p>
            <h2 className="font-heading text-4xl font-bold text-slate-900 uppercase">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="font-heading text-6xl font-bold text-brand-100 mb-3">{s.step}</div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-500 text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A183D]">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="font-heading text-4xl font-bold text-white uppercase mb-4">Ready to Ship?</h2>
          <p className="text-slate-300 mb-8">Create your account and start booking shipments with the most reliable logistics partner.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/auth/register" data-testid="cta-signup-btn" className="px-8 py-3 bg-[#F97316] text-white font-semibold uppercase rounded-sm hover:bg-orange-600 transition-colors">
              Get Started Free
            </Link>
            <Link to="/book" className="px-8 py-3 border border-white/30 text-white font-semibold uppercase rounded-sm hover:bg-white/10 transition-colors">
              Book Without Account
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
